/**
 * Terminals over HTTP (open, control, close, list), with demo data in mock mode. The live
 * bytes go over the WebSocket in connection.ts.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { TERM_EXT } from '@orgus/protocol';
import type { TermControlRequest, TermOpenRequest, TermOpenResponse, TermState, TermSummary } from '@orgus/protocol';
import { apiDelete, apiGet, apiPost, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { mockTerms } from './mock.ts';

interface TermsStore {
  terms: TermSummary[];
  phase: 'loading' | 'ready' | 'error';
  error: UiError | null;
  /** Components that want the list; polling runs while any does. */
  users: number;
  set: (s: Partial<Omit<TermsStore, 'set'>>) => void;
}

export const useTermsStore = create<TermsStore>()((set) => ({
  terms: [],
  phase: 'loading',
  error: null,
  users: 0,
  set: (s) => set(s),
}));

const isMock = () => useOrgus.getState().mode === 'mock';

export async function refreshTerms(): Promise<void> {
  const st = useTermsStore.getState();
  if (isMock()) {
    st.set({ terms: mockTerms.list(), phase: 'ready', error: null });
    return;
  }
  try {
    const r = await apiGet<TermState>('/api/term');
    st.set({ terms: r.terms, phase: 'ready', error: null });
  } catch (e) {
    const error = toUiError(e, { what: 'Could not load the terminals.', next: 'Check that the hub is running, then try again.' });
    // Keep what we had: the list stays usable while the hub is briefly away.
    st.set(st.terms.length ? { error } : { phase: 'error', error });
  }
}

let unsubscribe: (() => void) | null = null;

/**
 * Every terminal this person may see, kept fresh while mounted. The hub broadcasts the
 * module's slice (ext 'term' 'state') on every change; the list itself is re-read from
 * GET /api/term, because only that answer is filtered by this person's role.
 */
export function useTerms(): { terms: TermSummary[]; phase: TermsStore['phase']; error: UiError | null; refresh: () => Promise<void> } {
  const terms = useTermsStore((s) => s.terms);
  const phase = useTermsStore((s) => s.phase);
  const error = useTermsStore((s) => s.error);
  const mode = useOrgus((s) => s.mode);
  useEffect(() => {
    const st = useTermsStore.getState();
    st.set({ users: st.users + 1 });
    void refreshTerms();
    if (!unsubscribe) {
      unsubscribe = useOrgus.subscribe((s, prev) => {
        if (s.ext[TERM_EXT] !== prev.ext[TERM_EXT] || (s.connection === 'live' && prev.connection !== 'live')) void refreshTerms();
      });
    }
    const off = mode === 'mock' ? mockTerms.subscribe(() => void refreshTerms()) : () => undefined;
    return () => {
      off();
      const left = useTermsStore.getState().users - 1;
      useTermsStore.getState().set({ users: left });
      if (left <= 0 && unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
    };
  }, [mode]);
  return { terms, phase, error, refresh: refreshTerms };
}

export async function openTerminal(req: TermOpenRequest): Promise<TermOpenResponse> {
  const r = isMock() ? mockTerms.open(req) : await apiPost<TermOpenResponse>('/api/term', req);
  await refreshTerms();
  return r;
}

export async function controlTerminal(id: string, body: TermControlRequest): Promise<TermSummary> {
  const r = isMock() ? mockTerms.control(id, body) : await apiPost<TermSummary>(`/api/term/${encodeURIComponent(id)}/control`, body);
  await refreshTerms();
  return r;
}

export async function closeTerminal(id: string): Promise<TermSummary> {
  const r = isMock() ? mockTerms.close(id) : await apiDelete<TermSummary>(`/api/term/${encodeURIComponent(id)}`);
  await refreshTerms();
  return r;
}

export function recordingUrl(id: string): string {
  return `/api/term/${encodeURIComponent(id)}/cast`;
}
